import type { V3 } from "@istari/istari-client";

import type {
  JobNotification,
  JobStatus,
  NotificationStatus,
  ResourceSharedNotification,
  SharedResourceType,
  SharedRole,
} from "~/notifications/model/notification";

export function toStatus(item: V3.InboxItemDto): NotificationStatus {
  if (item.archived) return "archived";
  if (item.read) return "read";
  return "unread";
}

export function toJobStatus(
  terminalState: V3.UserJobTerminalStateNotificationEvent["terminalState"],
): JobStatus | null {
  switch (terminalState) {
    case "Completed":
      return "completed";
    case "Failed":
      return "failed";
    case "Canceled":
      return null;
    default:
      return null;
  }
}

export function toSharedRole(permissionLevel: string): SharedRole {
  switch (permissionLevel) {
    case "editor":
    case "owner":
    case "administrator":
    case "executor":
      return "editor";
    case "viewer":
      return "viewer";
    default:
      console.error("[notifications] unknown permission level", permissionLevel);
      return "viewer";
  }
}

export function toSharedResourceType(resourceType: V3.ResourceTypeDto | null | undefined): SharedResourceType | null {
  if (!resourceType) return null;
  switch (resourceType) {
    case "artifact":
      return "artifact";
    case "model":
      return "model";
    case "file":
      return "file";
    default:
      // "system" is absent from ResourceTypeDto — systems are fetched via listSystems, not the
      // resource catalog, so access-granted events for systems don't resolve a ResourceTypeDto.
      return null;
  }
}

export function toShareNotification(
  item: V3.InboxItemDto,
  data: V3.UserAccessGrantedNotificationEvent,
  actorName: string | null,
  resource: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById?: string | null } | null,
): ResourceSharedNotification {
  const actorId = resource?.createdById ?? data.grantedUserId;
  return {
    id: item.id,
    type: "share",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: {
      userId: actorId,
      displayName: actorName ?? "Unknown user",
    },
    payload: {
      // Falls back to "file" for unknown types including system IDs — the event payload does not
      // include resourceType, and systems resolve via listSystems rather than the resource catalog.
      // Requires backend to add resourceType to UserAccessGrantedNotificationEvent to fix routing.
      resourceType: toSharedResourceType(resource?.resourceType) ?? "file",
      resourceId: data.resourceId,
      resourceName: resource?.name ?? data.resourceId,
      role: toSharedRole(data.permissionLevel),
      shareCount: 1,
    },
  };
}

export function toJobNotification(
  item: V3.InboxItemDto,
  data: V3.UserJobTerminalStateNotificationEvent,
): JobNotification | null {
  const jobStatus = toJobStatus(data.terminalState);
  if (!jobStatus) return null;

  return {
    id: item.id,
    type: "job",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: { userId: "system", displayName: "Build Pipeline" },
    payload: {
      jobId: data.jobId,
      jobStatus,
      functionName: `Job ${data.jobId.slice(0, 8)}`,
    },
  };
}
